/**
 * Server-only: customer update webhook.
 * Used by API routes; do not import from client components.
 * Matches DB fields: first_name, last_name, photo, email, phone, billing_details (jsonb).
 * Password is updated via a separate change-password flow.
 */

import {
  errorCodeFromBody,
  errorMessageFromBody,
  isApplicationErrorPayload,
} from "./server-response";
import { fetchWrapper } from "~/lib/fetch";

/** Never forward these to customer-update (password changes use another flow). */
const SENSITIVE_UPDATE_KEYS = new Set([
  "password",
  "password_confirmation",
  "current_password",
  "new_password",
  "confirm_password",
]);

/**
 * Remove password-related fields from a customer-update JSON body (top-level and
 * nested `billing_details`). Safe to call from the API route and from client `updateProfile`.
 */
export function sanitizeCustomerUpdateBody(
  body: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(body)) {
    if (SENSITIVE_UPDATE_KEYS.has(k.toLowerCase())) continue;
    out[k] = v;
  }
  const bd = out.billing_details;
  if (bd && typeof bd === "object" && !Array.isArray(bd)) {
    const copy = { ...(bd as Record<string, unknown>) };
    for (const key of Object.keys(copy)) {
      if (SENSITIVE_UPDATE_KEYS.has(key.toLowerCase())) delete copy[key];
    }
    out.billing_details = copy;
  }
  return out;
}

export interface CustomerUpdateWebhookPayload {
  customer_id: string;
  first_name?: string;
  last_name?: string;
  photo?: string;
  email?: string;
  phone?: string;
  /** Billing/details as JSON (e.g. address1, address2, country) */
  billing_details?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface CustomerUpdateWebhookResult {
  ok: boolean;
  error?: string;
  code?: number;
  data?: unknown;
}

export async function updateCustomerWithWebhook(
  webhookBase: string,
  payload: CustomerUpdateWebhookPayload,
  token?: string,
): Promise<CustomerUpdateWebhookResult> {
  const base = webhookBase.replace(/\/$/, "");
  const url = `${base}/customer-update`;
  const safePayload = sanitizeCustomerUpdateBody(
    payload as Record<string, unknown>,
  ) as CustomerUpdateWebhookPayload;
  const res = await fetchWrapper(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(safePayload),
  });
  const text = await res.text();
  let data: Record<string, unknown> = {};
  try {
    data = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  } catch {
    data = { raw: text } as unknown as Record<string, unknown>;
  }
  if (!res.ok) {
    return {
      ok: false,
      error: errorMessageFromBody(data, "Update failed"),
      code: errorCodeFromBody(data),
    };
  }
  if (isApplicationErrorPayload(data)) {
    return {
      ok: false,
      error: errorMessageFromBody(data, "Update failed"),
      code: errorCodeFromBody(data),
    };
  }
  return { ok: true, data };
}
